/**
 * InspectionEvidenceDrawer - Seletor compartilhado de evidência da vistoria.
 *
 * Exibe a foto modelo congelada no item e oferece somente as fontes liberadas
 * pela configuração do template. A captura e o upload continuam nos fluxos
 * chamadores, preservando as validações específicas de cada canal.
 *
 * CHANGELOG:
 *   RELEASE: 2026-09-28 v1.0.0 - Criação inicial para vistoria interna e link externo.
 */
import { Camera, ExternalLink, Image as ImageIcon, Paperclip, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Drawer, DrawerClose, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerTitle,
} from '@/components/ui/drawer';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  itemName: string;
  description?: string | null;
  referenceUrl?: string | null;
  allowCamera: boolean;
  allowFile: boolean;
  fileLabel?: string;
  loading?: boolean;
  onCamera: () => void;
  onFile: () => void;
}

export function InspectionEvidenceDrawer({
  open, onOpenChange, itemName, description, referenceUrl, allowCamera, allowFile,
  fileLabel = 'Escolher foto ou arquivo', loading, onCamera, onFile,
}: Props) {
  return (
    <Drawer open={open} onOpenChange={onOpenChange} shouldScaleBackground={false}>
      <DrawerContent className="mx-auto max-h-[92dvh] max-w-xl overflow-hidden">
        <DrawerHeader className="border-b px-5 pb-4 pt-2 text-left">
          <DrawerTitle className="text-base">Adicionar evidência</DrawerTitle>
          <DrawerDescription className="text-xs">{itemName}</DrawerDescription>
        </DrawerHeader>

        <div className="space-y-4 overflow-y-auto px-5 py-4">
          {referenceUrl && (
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <p className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
                  <ImageIcon className="h-3.5 w-3.5 text-primary" /> Foto modelo
                </p>
                <Button type="button" variant="ghost" size="xs" className="h-7"
                  onClick={() => window.open(referenceUrl, '_blank', 'noopener,noreferrer')}>
                  Ampliar <ExternalLink className="ml-1 h-3.5 w-3.5" />
                </Button>
              </div>
              <Button type="button" variant="outline" className="block h-auto w-full overflow-hidden p-0"
                onClick={() => window.open(referenceUrl, '_blank', 'noopener,noreferrer')} aria-label="Ampliar foto modelo">
                <img src={referenceUrl} alt={`Foto modelo de ${itemName}`} className="aspect-video w-full object-contain" />
              </Button>
              <p className="text-xs text-muted-foreground">Use esta imagem como referência para enquadramento e posição.</p>
            </div>
          )}

          {description && <p className="border-l-2 border-primary pl-3 text-sm text-muted-foreground">{description}</p>}

          <div className="grid gap-2">
            {allowCamera && (
              <Button type="button" className="h-12 justify-start" disabled={loading} onClick={onCamera}>
                <Camera className="mr-2 h-5 w-5" /> Tirar foto
              </Button>
            )}
            {allowFile && (
              <Button type="button" variant="outline" className="h-12 justify-start" disabled={loading} onClick={onFile}>
                <Paperclip className="mr-2 h-5 w-5" /> {fileLabel}
              </Button>
            )}
          </div>
        </div>

        <DrawerFooter className="border-t px-5 py-3">
          <DrawerClose asChild>
            <Button type="button" variant="ghost" disabled={loading}><X className="mr-2 h-4 w-4" /> Fechar</Button>
          </DrawerClose>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}